import { ThemeProvider } from '@emotion/react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { createTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { getUserLikesAndViews } from 'APIs/UserDataApi';
import { handleFollowing } from 'APIs/utils';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import IC_CONTACT from 'assets/icons/ic_contact_white.svg';
import IC_USER from 'assets/icons/ic_person_white.svg';
import NormalButton from 'components/atoms/Button/NormalButton';
import SmallButton from 'components/atoms/Button/SmallButton';
import FollowUserModal from 'components/molecules/FollowModal';
import SimpleInputText from 'components/molecules/InputText/SimpleInputText';
import SocialMediaInput from 'components/molecules/SocialMediaInput/SocialMediaInput';
import { selectAuth } from 'features/auth/authSlice';
import { UserData, UserProfileData } from 'interfaces/UserData.interface';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onContact } from 'utils/Firebase.util';
import { v4 } from 'uuid';

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

const ParaText = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  color: '#C1C1C1',
}));

const SmallText = styled(Typography)(({ theme }) => ({
  fontSize: '.8rem',
  color: '#ffffff',
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontSize: '2.2rem',
  fontWeight: 'bold',
  color: '#ffffff',
  [colortheme.breakpoints.down('lg')]: {
    fontSize: '1.8rem',
  },
  [colortheme.breakpoints.down('md')]: {
    fontSize: '1.4rem',
  },
  [colortheme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const Icon = styled('img')(({ theme }) => ({
  width: '16px',
  height: '16px',
  marginRight: '10px',
}));

const CustomTextBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  cursor: 'pointer',
  padding: '10px',
  borderRadius: '15px',
  ':hover': {
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
}));

interface ProfileSidebarProps {
  editing?: boolean;
  data: UserProfileData;
  setData: React.Dispatch<React.SetStateAction<UserProfileData>>;
}

const OtherProfileSidebar: React.FC<ProfileSidebarProps> = ({
  editing,
  data,
  setData,
}) => {
  const verifySvg = require('assets/icons/verify.svg');
  const twitterSvg = require('assets/icons/twitter.svg');
  const facebookSvg = require('assets/icons/facebook.svg');
  const instagramSvg = require('assets/icons/instagram.svg');
  const tiktokSvg = require('assets/icons/tiktok.svg');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [nameFontSize, setNameFontSize] = useState<number>(22);
  const [likes, setLikes] = useState<number>(0);
  const [views, setViews] = useState<number>(0);
  const [following, setFollowing] = useState<boolean>(false);
  const authState = useAppSelector(selectAuth);

  const onChangePlatformInput = (value: string, platform: string) => {
    setData(data => ({ ...data, [platform]: value }));
  };

  const isOtherUser = (): boolean => {
    return authState.id !== data.id;
  };

  useEffect(() => {
    if (data.username.length > 22) {
      setNameFontSize(22);
    } else if (data.username.length > 16) {
      setNameFontSize(16);
    } else {
      setNameFontSize(32);
    }
  }, [data.username]);

  useEffect(() => {
    const getFollowing = () => {
      if (authState && authState.following) {
        authState.following.some(
          following =>
            following.following.id === data.id && !following.unfollowed
        ) && setFollowing(true);
      }
    };
    getFollowing();
    const getTotalLikesAndViews = async () => {
      const res = await getUserLikesAndViews(data.username);
      setLikes(res.likes);
      setViews(res.views);
    };
    getTotalLikesAndViews();
    //eslint-disable-next-line
  }, [authState.following, data]);

  const handleFollowUser = async () => {
    if (isOtherUser()) {
      if (following) {
        data.followers = data.followers.filter(
          follower => follower.follower.id !== authState.id
        );
      } else {
        data.followers.push({
          id: v4(),
          followedAt: new Date(),
          followedDate: new Date(),
          follower: authState as UserData,
          following: data as UserData,
        });
      }
      await handleFollowing({
        authState,
        data: data as UserData,
        following,
        setFollowing,
        dispatch,
      });
    }
  };

  const getFollowLength = useCallback(
    (type: 'followers' | 'following') => {
      if (type === 'followers') {
        if (isOtherUser()) {
          return data.followers.length;
        } else if (authState.followers && !isOtherUser()) {
          return authState.followers.length;
        }
      } else {
        if (isOtherUser()) {
          return data.following.length;
        } else if (authState.following && !isOtherUser()) {
          let unfollowLength = 0;
          authState.following.forEach(
            following => following.unfollowed && unfollowLength++
          );
          return authState.following.length - unfollowLength;
        }
      }
    },
    //eslint-disable-next-line
    [authState.following, authState.id]
  );

  return (
    <React.Fragment>
      <ThemeProvider theme={colortheme}>
        <Box sx={{ minWidth: '200px' }}>
          {editing ? (
            <>
              <ParaText>
                <strong>Name:</strong>
              </ParaText>
              <SimpleInputText
                initValue={data.name}
                setInput={val => {
                  setData(data => ({ ...data, name: val }));
                }}
              />
            </>
          ) : (
            <TitleText
              style={{
                display: 'flex',
                alignItems: 'center',
                margin: '1.5rem 0',
                fontSize: `${nameFontSize}px`,
                whiteSpace: 'nowrap',
              }}
            >
              {data.name}
              {data.verified && (
                <img
                  src={verifySvg.default}
                  alt=""
                  width="30px"
                  height="30px"
                  style={{ marginLeft: '15px' }}
                />
              )}
            </TitleText>
          )}
          {editing ? (
            <>
              <ParaText>
                <strong>Username:</strong>
              </ParaText>
              <SimpleInputText
                initValue={data.username}
                setInput={val => {
                  setData(data => ({ ...data, username: val }));
                }}
              />
            </>
          ) : (
            <ParaText>@{data.username}</ParaText>
          )}
          <SmallButton
            text={'alpha tester'}
            color={'primary'}
            style={{ padding: '.5rem 1.5rem', margin: '1.5rem 0rem' }}
          />
          {editing && (
            <ParaText>
              <strong>Bio</strong>
            </ParaText>
          )}
          {editing ? (
            <SimpleInputText
              initValue={data.bio || ''}
              multiline
              setInput={val => {
                setData(data => ({ ...data, bio: val }));
              }}
            />
          ) : (
            <ParaText>{data.bio}</ParaText>
          )}
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              margin: '1.5rem 0px',
              flexDirection: { lg: 'row', xs: 'column' },
              gap: '10px',
            }}
          >
            {isOtherUser() && (
              <>
                {' '}
                <Grid item xs={12} sm={5.8}>
                  <NormalButton
                    text={following ? 'Unfollow' : 'Follow'}
                    style={{ width: '100%' }}
                    Icon={<Icon src={IC_USER} alt="ic-contact" />}
                    onClick={handleFollowUser}
                  />
                </Grid>
                <Grid item xs={12} sm={5.8}>
                  <NormalButton
                    onClick={() =>
                      onContact(authState, data, dispatch, navigate)
                    }
                    text={'Contact'}
                    color={'yellow'}
                    style={{ width: '100%' }}
                    Icon={<Icon src={IC_CONTACT} alt="ic-user" />}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <Grid
            container
            style={{
              margin: '2rem 0rem',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <Grid item xs={3}>
              <FollowUserModal
                data={data}
                isOtherUser={isOtherUser()}
                updateComponent={handleFollowUser}
                type="followers"
              >
                <CustomTextBox>
                  <TitleText> {getFollowLength('followers')}</TitleText>
                  <SmallText style={{ color: '#ddb930' }}>followers</SmallText>
                </CustomTextBox>
              </FollowUserModal>
            </Grid>
            <Grid item xs={3}>
              <FollowUserModal
                data={data}
                isOtherUser={isOtherUser()}
                updateComponent={handleFollowUser}
                type="following"
              >
                <CustomTextBox>
                  <TitleText> {getFollowLength('following')}</TitleText>
                  <SmallText style={{ color: '#ddb930' }}>following</SmallText>
                </CustomTextBox>
              </FollowUserModal>
            </Grid>
          </Grid>
          <Grid container style={{ margin: '2rem 0rem' }}>
            <Grid item>
              <CustomTextBox>
                <ParaText>Website</ParaText>

                {editing ? (
                  <SimpleInputText
                    initValue={data.website || ''}
                    setInput={val => {
                      setData(data => ({ ...data, website: val }));
                    }}
                  />
                ) : (
                  <a
                    href={`//${data.website}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <SmallText style={{ color: '#c1c1c1' }}>
                      {data.website}
                    </SmallText>
                  </a>
                )}
              </CustomTextBox>
            </Grid>
          </Grid>
          <Grid container style={{ margin: '2rem 0rem', display: 'flex' }}>
            <Grid item xs={12} style={{ marginBottom: '1rem' }}>
              <ParaText>Stats</ParaText>
            </Grid>

            {/* <Grid item xs={4}>
              <CustomTextBox>
                <TitleText>13%</TitleText>
                <SmallText style={{ color: '#ddb930' }}>Evolution</SmallText>
              </CustomTextBox>
            </Grid> */}
            <Grid item xs={4}>
              <CustomTextBox>
                <TitleText>{likes}</TitleText>
                <SmallText style={{ color: '#ddb930' }}>Likes</SmallText>
              </CustomTextBox>
            </Grid>

            <Grid item xs={4}>
              <CustomTextBox>
                <TitleText>{views}</TitleText>
                <SmallText style={{ color: '#ddb930' }}>Views</SmallText>
              </CustomTextBox>
            </Grid>
            {/* <Grid item xs={4}>
              <CustomTextBox>
                <TitleText>648</TitleText>
                <SmallText style={{ color: '#ddb930' }}>Points</SmallText>
              </CustomTextBox>
            </Grid> */}
          </Grid>
          <Grid container style={{ margin: '2rem 0rem' }}>
            <Grid item xs={12} style={{ marginBottom: '1rem' }}>
              {(data.facebook ||
                data.twitter ||
                data.tiktok ||
                data.instagram ||
                editing) && <ParaText>Social networks</ParaText>}
            </Grid>

            <SocialMediaInput
              value={data.facebook || ''}
              platform="facebook"
              platformImg={facebookSvg.default}
              changeValue={onChangePlatformInput}
              editing={!!editing}
            />

            <SocialMediaInput
              value={data.tiktok || ''}
              platform="tiktok"
              platformImg={tiktokSvg.default}
              changeValue={onChangePlatformInput}
              editing={!!editing}
            />

            <SocialMediaInput
              value={data.instagram || ''}
              platform="instagram"
              platformImg={instagramSvg.default}
              changeValue={onChangePlatformInput}
              editing={!!editing}
            />
            <SocialMediaInput
              value={data.twitter || ''}
              platform="twitter"
              platformImg={twitterSvg.default}
              changeValue={onChangePlatformInput}
              editing={!!editing}
            />
          </Grid>
        </Box>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default OtherProfileSidebar;
